/* eslint-disable react/no-array-index-key */
import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import StepWrapper from '../../../../../shared/stepwrapper';
import AiSequenceModalFooter from '../ai-sequence-modal-footer/ai-sequence-modal-footer';
import AccordionItem, {
  MCQOption,
} from '../../../../../shared/accordion-card/mcqaccordioncard';
import {
  resetHasStepSkipped,
  setHasAiSeqStepSkipped,
} from '../../../../../sequence-slice';
import { RootState } from '../../../../../../../store/root-reducer';
import { truncate } from '../../../../../../../shared/utils';

interface RefineYourGoalStepProps {
  handleModalClose: () => void;
  goalRefinementQuestions: {
    question: string;
    options: { text: string }[];
  }[];
  saveCurrentStepDetails?: (payload: any) => void;
}

/**
 *
 * @prop goalRefinementQuestions - It will show the GoalRefinementQuestions fetch from ai.
 * @prop saveCurrentStepDetails - Function to save current step details.
 *
 * @returns React Component
 */

const RefineYourGoalStep: React.FC<RefineYourGoalStepProps> = ({
  goalRefinementQuestions = [],
  saveCurrentStepDetails,
}) => {
  const [expandedId, setExpandedId] = useState<number | null>(0);
  const [selectionState, setSelectionState] = useState<
    Record<number, MCQOption[]>
  >({});
  const [fieldErrors, setFieldErrors] = useState<Record<number, string>>({});
  const { data } = useSelector(
    (state: RootState) => state?.sequence?.generateAiSeqModal,
  );

  const handleSelectionChange = useCallback(
    (index: number, selectedOptions: MCQOption[]) => {
      setSelectionState((prev) => ({ ...prev, [index]: selectedOptions }));
    },
    [],
  );
  const dispatch = useDispatch();

  const handleContinue = () => {
    const payload = goalRefinementQuestions?.map((question, index) => ({
      question: question?.question?.trim(),
      options: (selectionState[index] || [])?.map((opt) => ({
        text: opt?.label?.trim(),
        ...(opt.isCustom ? { isCustom: true } : {}),
      })),
    }));

    saveCurrentStepDetails({ goalRefinementQuestions: payload });
  };

  const disableContinueBtn = useMemo(() => {
    // Check if any question has an error
    const hasErrors = Object.values(fieldErrors)?.some(Boolean);

    // Check if at least one checkbox is selected across all questions
    const atLeastOneSelected = Object.values(selectionState)?.some(
      (selected) => selected?.length > 0,
    );

    return hasErrors || !atLeastOneSelected;
  }, [fieldErrors, selectionState]);

  return (
    <StepWrapper
      heading={`Set goal for ${truncate(data?.companyInfo?.name, 26, false)}`}
    >
      {goalRefinementQuestions?.map((question, index) => (
        <AccordionItem
          key={index}
          id={index}
          title={`${index + 1}. ${question?.question}`}
          options={question?.options?.map((opt, i) => ({
            id: i + 1,
            label: opt?.text,
            isCustom: false,
          }))}
          selectedOptions={selectionState[index] || []}
          onSelectionChange={(options) => handleSelectionChange(index, options)}
          expandedId={expandedId}
          setExpandedId={setExpandedId}
          fieldErrors={fieldErrors}
          setFieldErrors={setFieldErrors}
        />
      ))}

      <AiSequenceModalFooter
        showSkipBtn={true}
        disableContinueBtn={disableContinueBtn}
        onClickContinue={handleContinue}
        onClickSkip={() => {
          dispatch(setHasAiSeqStepSkipped(true));
          saveCurrentStepDetails({ hasSkipped: true });
          dispatch(resetHasStepSkipped());
        }}
      />
    </StepWrapper>
  );
};

export default memo(RefineYourGoalStep);
